import { apiDirectoryService, apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import { objectsRHandler, DEFAULT_PARAMS } from '@/utils/objectsRHandler';
import { getApiData, API_TYPES } from '@/utils/modelsApi';
import { ROLES } from '@/constants/roles';

import Event from '../list/event';
import Participant from '../list/participant';
import EventAttribute from '../list/event-attribute';
import { getDownloadLinkByType } from '@/helpers/files';
import { TYPE } from '@/constants/filesExtensions';
import EventDocument from '../list/event-document';
import { assertDocumentResponse, saveEventDocumentFiles } from '../event-document-files';
import BaseModel from '../list/base';
import ParticipantSubordinated from '../list/participant-subordinated';
import EventActivity from '../list/eventActivity/index';
import AwardSettings from '../list/award-settings/index';
import ParticipantExpeled from '../list/participant-expeled';
import EventRecommendation from '../list/event/recommendation';
import EventRecommendationWeight from '../list/event/recommendation/weight';
import EventStatistic from '../list/event/statistic';

const PRIVATE_ROLES = [ROLES.organization, ROLES.event_manager, ROLES.admin];

const API_DATA = {
  [API_TYPES.private]: {
    path: 'events',
    get objects() {
      const { path } = this;
      const getSubordinated = (serialize, { args = [], params } = DEFAULT_PARAMS) => {
        const [id] = args;
        return rHandler(
          apiService.get(`/${path}/${id}/members/subordinated`, params),
          (response) => ({ response, items: response.data.data.map(serialize) }),
          (error) => ({ error }),
        );
      };
      return {
        ...objectsRHandler(path, Event),

        getMembers({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/members`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        getMembersByHigher({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/members/by-higher`, params),
            (response) => ({ response, items: response.data.data }),
            (error) => ({ error }),
          );
        },
        getMembersByNomination({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/members/by-nomination`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        getMembersCached({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.getCached(`/${path}/${id}/members`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        getMembersSubordinated(payload = DEFAULT_PARAMS) {
          return getSubordinated((item) => new ParticipantSubordinated(item, ''), payload);
        },
        getSubordinatedApplications(payload = DEFAULT_PARAMS) {
          return getSubordinated((item) => Participant.fromSubordinated(item, 'members'), payload);
        },
        getMembersExpeled({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/expel-users`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new ParticipantExpeled(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        getMembersSubordinatedByLevel({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/subordination_my_members`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        expelMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/expel-user`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        rejectMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/reject`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        confirmMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/confirm`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        considerMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/consider`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        incorrectDocumentsMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/incorrect-documents`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        attachGroupMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/set-group`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        changeGroupMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/change-group`, data, { handleForbiddenLocally: true }),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        detachGroupMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/detach-group`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        getAttributes({ args = [], params } = DEFAULT_PARAMS) { // search[nomination_id]
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/attributes`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new EventAttribute(i, 'event-global-attributes')),
            }),
            (error) => ({ error }),
          );
        },
        returnMembers({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/restore-expel-users`, data), // restore_ids: [1,2,3,4]
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        removeExpeled({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/delete-expel-users`, data), // restore_ids: [1,2,3,4]
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        fullExpel({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/expel`, data), // user_ids: [1,2,3,4]
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        massClose({ data } = DEFAULT_PARAMS) {
          return rHandler(
            apiService.put(`/${path}/mass-close`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
      };
    },
  },
  [API_TYPES.public]: {
    path: 'public/events',
    get objects() {
      const { path } = this;
      return {
        ...objectsRHandler(path, Event),
      };
    },
  },
};

export const EventManager = {
  get objects() {
    return {
      ...getApiData(API_DATA, PRIVATE_ROLES).objects,
      [API_TYPES.private]: API_DATA[API_TYPES.private].objects,
      [API_TYPES.public]: API_DATA[API_TYPES.public].objects,
      indexNew({ params, obs } = DEFAULT_PARAMS) {
        const path = 'public/events/search2';
        return rHandler(
          apiService.get(path, params),
          (response) => ({
            response,
            items: response.data.data.map((i) => new Event(i, 'public/events')),
          }),
          (error) => ({ error }),
          obs,
        );
      },
    };
  },
  get attributes() {
    const path = 'event-global-attributes';
    return {
      ...objectsRHandler(path, EventAttribute),
      massDelete({ data } = DEFAULT_PARAMS) { // { attribute_ids: [1671] }
        return rHandler(
          apiService.post(`/${path}/mass-delete`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      attach({ args = [], data } = DEFAULT_PARAMS) { // attach: [{ event_id: 9822, nomination_id: 18972, options: { required: false } }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/attach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      updateAssignmentOptions({ args = [], data, obs } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.put(`/${path}/${id}/assignment-options`, data),
          (response) => ({ response }),
          (error) => ({ error }),
          obs,
        );
      },
      detach({ args = [], data } = DEFAULT_PARAMS) { // detach: [{ event_id: 9822, nomination_id: 18972 }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/detach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      getTemplateForList({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.getCached('/event-global-attributes/import-template', params),
          (response) => ({
            response,
            item: response.data.data, // { file: Base64-String }
            get objectUrl() {
              const { item } = this;
              if (!item?.file) return null;
              return getDownloadLinkByType(item.file, 'Пример', TYPE.xlsx);
            },
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get attributesValues() {
    const path = 'event-attribute-values';
    return {
      ...objectsRHandler(path, BaseModel),
      massStore({ data, obs } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.post(`${path}/mass-store`, data),
          (response) => ({ response }),
          (error) => ({ error }),
          obs,
        );
      },
    };
  },
  get documents() {
    const path = 'event-directory-document';
    return {
      ...objectsRHandler(path, EventDocument),
      upload({ args = [], data, obs } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.post(`/${path}/${args[0]}/upload`, data, { handleForbiddenLocally: true }),
          (response) => {
            try {
              return assertDocumentResponse({ response, item: response.data.data });
            } catch (error) {
              return { error, response };
            }
          },
          (error) => ({ error: error || new Error('Не удалось сохранить файл') }),
          obs,
        );
      },
      removeFile({ args = [], obs } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.delete(`/documents/${args[0]}`, { handleForbiddenLocally: true }),
          (response) => ({ response }),
          (error) => ({ error: error || new Error('Не удалось удалить файл') }),
          obs,
        );
      },
      async saveFiles({ args = [], data, obs, onProgress, isActive }) {
        try {
          const files = await saveEventDocumentFiles({
            files: data.files,
            initial: data.initial,
            limit: data.doc_count,
            upload: (file) => this.upload({
              args,
              data: {
                document: EventDocument.serializeFile(file),
                ...data.user_id != null ? { user_id: data.user_id } : {},
              },
              obs,
            }),
            remove: (file) => this.removeFile({ args: [file.id], obs }),
            onProgress,
            isActive,
          });
          return { files };
        } catch (error) {
          return { error };
        }
      },
      attach({ args = [], data } = DEFAULT_PARAMS) { // attach: [{ event_id: 9822, nomination_id: 18972, options: { text: 1234 } }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/attach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      updateAssignmentOptions({ args = [], data, obs } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.put(`/${path}/${id}/assignment-options`, data),
          (response) => ({ response }),
          (error) => ({ error }),
          obs,
        );
      },
      detach({ args = [], data } = DEFAULT_PARAMS) { // detach: [{ event_id: 9822, nomination_id: 18972 }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/detach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      getDirectories({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiDirectoryService.getCached('/directory-event-documents', params),
          (response) => ({
            response,
            items: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get activities() {
    const eventPath = 'events';
    return {
      create({ args = [], data } = DEFAULT_PARAMS) {
        const [eventId] = args;
        const url = `/${eventPath}/${eventId}/activities`;
        return rHandler(
          apiService.post(url, data),
          (response) => ({ item: new EventActivity(response?.data?.data || {}, url), response }),
          (error) => ({ error }),
        );
      },
    };
  },
  get awardSettings() {
    const eventPath = 'events';
    return {
      index({ args = [], params } = DEFAULT_PARAMS) {
        const [eventId] = args;
        const url = `/${eventPath}/${eventId}/award-settings`;
        return rHandler(
          apiService.get(url, params),
          (response) => ({
            response,
            // items: response.data.data.map((i) => new AwardSettings(i, url)),
          }),
          (error) => ({ error }),
        );
      },
      create({ args = [], data } = DEFAULT_PARAMS) {
        const [eventId] = args;
        const url = `/${eventPath}/${eventId}/award-settings`;
        return rHandler(
          apiService.post(url, data),
          (response) => ({ item: new AwardSettings(response?.data?.data || {}, url), response }),
          (error) => ({ error }),
        );
      },
    };
  },
  get recommendations() {
    const path = 'event-recommendations';
    return {
      index({ params } = DEFAULT_PARAMS) {
        const url = `/${path}`;
        return rHandler(
          apiService.get(url, params),
          (response) => ({
            response,
            items: response.data.data.map((i) => new EventRecommendation(i, url)),
          }),
          (error) => ({ error }),
        );
      },
      get weights() {
        const weightsPath = `admin/${path}/weights`;
        return {
          get({ params } = DEFAULT_PARAMS) {
            const url = `/${weightsPath}`;
            return rHandler(
              apiService.get(url, params),
              (response) => ({
                response,
                item: new EventRecommendationWeight(response.data.data, url),
              }),
              (error) => ({ error }),
            );
          },
          set({ data } = DEFAULT_PARAMS) {
            const url = `/${weightsPath}`;
            return rHandler(
              apiService.put(url, data),
              (response) => ({
                response,
                item: new EventRecommendationWeight(response.data.data, url),
              }),
              (error) => ({ error }),
            );
          },
        };
      },
    };
  },
  get statistics() {
    const path = 'event-statistics';
    return {
      index({ params } = DEFAULT_PARAMS) {
        const url = `/${path}`;
        return rHandler(
          apiService.get(url, params),
          (response) => ({
            response,
            items: response.data.data.map((i) => new EventStatistic(i, url)),
          }),
          (error) => ({ error }),
        );
      },
      recalculate({ args, data } = DEFAULT_PARAMS) {
        const [eventId] = args;
        const url = `/${path}/recalculate/${eventId}`;
        return rHandler(
          apiService.post(url, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
    };
  },
};
